'use client';
import React, { useState, useEffect } from 'react';

// ─── Types ────────────────────────────────────────────────────────────────────
interface TrackingPreferences {
  scanTracking: boolean;
  downloadTracking: boolean;
  linkTracking: boolean;
  deviceTracking: boolean;
  locationTracking: boolean;
  anonymizeData: boolean;
  retentionDays: number;
}

interface HistoryRecord {
  id: string;
  type: string;
  content: string;
  createdAt: string;
  downloadCount: number;
  options?: {
    fgColor: string;
    bgColor: string;
    style: string;
    size: number;
    errorLevel: string;
  };
}

const DEFAULT_PREFS: TrackingPreferences = {
  scanTracking: true,
  downloadTracking: true,
  linkTracking: true,
  deviceTracking: false,
  locationTracking: false,
  anonymizeData: false,
  retentionDays: 90,
};

const MOCK_HISTORY: HistoryRecord[] = [
  { id: 'qr-001', type: 'url', content: 'https://knowourindia.com', createdAt: '2026-08-03T10:22:00Z', downloadCount: 7, options: { fgColor: '#0F172A', bgColor: '#FFFFFF', style: 'rounded', size: 512, errorLevel: 'M' } },
  { id: 'qr-002', type: 'upi', content: 'upi://pay?pa=novamax@okaxis', createdAt: '2026-08-03T09:15:00Z', downloadCount: 3, options: { fgColor: '#1E40AF', bgColor: '#EFF6FF', style: 'rounded', size: 1024, errorLevel: 'H' } },
  { id: 'qr-003', type: 'whatsapp', content: 'https://wa.me/919876543210', createdAt: '2026-08-02T16:45:00Z', downloadCount: 12, options: { fgColor: '#166534', bgColor: '#F0FDF4', style: 'rounded', size: 512, errorLevel: 'M' } },
  { id: 'qr-004', type: 'wifi', content: 'WIFI:T:WPA;S:NovaMax_Office_5G', createdAt: '2026-08-02T14:30:00Z', downloadCount: 2, options: { fgColor: '#92400E', bgColor: '#FFFBEB', style: 'square', size: 256, errorLevel: 'L' } },
  { id: 'qr-005', type: 'email', content: 'mailto:hello@knowourindia.com', createdAt: '2026-08-01T11:20:00Z', downloadCount: 5, options: { fgColor: '#4C1D95', bgColor: '#F5F3FF', style: 'rounded', size: 512, errorLevel: 'Q' } },
  { id: 'qr-006', type: 'url', content: 'https://novamaxstudios.com/portfolio', createdAt: '2026-08-01T09:05:00Z', downloadCount: 18, options: { fgColor: '#0F172A', bgColor: '#FFFFFF', style: 'rounded', size: 512, errorLevel: 'M' } },
  { id: 'qr-007', type: 'maps', content: 'https://maps.google.com/?q=India+Gate', createdAt: '2026-07-31T15:00:00Z', downloadCount: 4, options: { fgColor: '#9F1239', bgColor: '#FFF1F2', style: 'rounded', size: 512, errorLevel: 'M' } },
  { id: 'qr-008', type: 'phone', content: 'tel:+919876543210', createdAt: '2026-07-31T12:40:00Z', downloadCount: 1, options: { fgColor: '#0E7490', bgColor: '#ECFEFF', style: 'square', size: 256, errorLevel: 'L' } },
  { id: 'qr-009', type: 'text', content: 'Certificate of Completion — NovaMax Studios', createdAt: '2026-07-30T10:00:00Z', downloadCount: 23, options: { fgColor: '#374151', bgColor: '#F9FAFB', style: 'rounded', size: 1024, errorLevel: 'H' } },
  { id: 'qr-010', type: 'url', content: 'https://knowourindia.com/states/rajasthan', createdAt: '2026-07-29T08:30:00Z', downloadCount: 9, options: { fgColor: '#0F172A', bgColor: '#FFFFFF', style: 'rounded', size: 512, errorLevel: 'M' } },
];

// ─── Toggle Component ─────────────────────────────────────────────────────────
function Toggle({ checked, onChange, disabled }: { checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <button
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className="relative flex-shrink-0 transition-all duration-200"
      style={{
        width: '44px',
        height: '24px',
        borderRadius: '12px',
        background: checked ? 'linear-gradient(135deg, #2563EB 0%, #7C3AED 100%)' : 'var(--border)',
        border: 'none',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        padding: 0,
        outline: 'none',
      }}
    >
      <span
        className="absolute top-0.5 transition-all duration-200"
        style={{
          width: '20px',
          height: '20px',
          borderRadius: '50%',
          background: '#FFFFFF',
          boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
          left: checked ? '22px' : '2px',
        }}
      />
    </button>
  );
}

// ─── Section Card ─────────────────────────────────────────────────────────────
function SectionCard({ title, description, icon, accent, children }: {
  title: string; description: string; icon: React.ReactNode; accent: string; children: React.ReactNode;
}) {
  return (
    <div className="bg-card rounded-3xl border border-border shadow-card overflow-hidden">
      <div className="px-6 py-5 border-b border-border flex items-center gap-4">
        <div className="w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: accent }}>
          {icon}
        </div>
        <div>
          <h2 className="font-bold text-foreground" style={{ fontSize: '15px' }}>{title}</h2>
          <p className="text-muted-foreground mt-0.5" style={{ fontSize: '12px' }}>{description}</p>
        </div>
      </div>
      <div className="px-6 py-5">{children}</div>
    </div>
  );
}

// ─── Tracking Row ─────────────────────────────────────────────────────────────
function TrackingRow({ label, description, checked, onChange, disabled }: {
  label: string; description: string; checked: boolean; onChange: (v: boolean) => void; disabled?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-4 py-3.5 border-b border-border last:border-0">
      <div className="min-w-0">
        <p className="font-semibold text-foreground" style={{ fontSize: '14px' }}>{label}</p>
        <p className="text-muted-foreground mt-0.5" style={{ fontSize: '12px' }}>{description}</p>
      </div>
      <Toggle checked={checked} onChange={onChange} disabled={disabled} />
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function SettingsClient() {
  const [prefs, setPrefs] = useState<TrackingPreferences>(DEFAULT_PREFS);
  const [saved, setSaved] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportSuccess, setExportSuccess] = useState(false);
  const [historyRecords, setHistoryRecords] = useState<HistoryRecord[]>(MOCK_HISTORY);

  useEffect(() => {
    const stored = localStorage.getItem('nova-qr-tracking-prefs');
    if (stored) {
      try { setPrefs(JSON.parse(stored)); } catch { /* ignore */ }
    }
    // Merge localStorage QR history with mock
    const storedHistory = localStorage.getItem('nova-qr-history');
    if (storedHistory) {
      try {
        const parsed = JSON.parse(storedHistory) as HistoryRecord[];
        const merged = [...parsed, ...MOCK_HISTORY].reduce((acc: HistoryRecord[], r) => {
          if (!acc.find(x => x.id === r.id)) acc.push(r);
          return acc;
        }, []);
        setHistoryRecords(merged.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()));
      } catch { /* ignore */ }
    }
  }, []);

  const updatePref = (key: keyof TrackingPreferences, value: boolean | number) => {
    setPrefs(prev => ({ ...prev, [key]: value }));
    setSaved(false);
  };

  const handleSave = () => {
    localStorage.setItem('nova-qr-tracking-prefs', JSON.stringify(prefs));
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const handleExportCSV = () => {
    setExportLoading(true);
    setTimeout(() => {
      const headers = ['ID', 'Type', 'Content', 'Created At', 'Downloads', 'FG Color', 'BG Color', 'Style', 'Size', 'Error Level'];
      const rows = historyRecords.map(r => [
        r.id,
        r.type,
        `"${r.content.replace(/"/g, '""')}"`,
        new Date(r.createdAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }),
        r.downloadCount,
        r.options?.fgColor ?? '',
        r.options?.bgColor ?? '',
        r.options?.style ?? '',
        r.options?.size ?? '',
        r.options?.errorLevel ?? '',
      ]);
      const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `nova-qr-history-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setExportLoading(false);
      setExportSuccess(true);
      setTimeout(() => setExportSuccess(false), 3000);
    }, 800);
  };

  const totalDownloads = historyRecords.reduce((s, r) => s + r.downloadCount, 0);
  const enabledCount = [prefs.scanTracking, prefs.downloadTracking, prefs.linkTracking, prefs.deviceTracking, prefs.locationTracking].filter(Boolean).length;

  return (
    <div className="min-h-screen">
      {/* ── Page Header ── */}
      <div className="px-6 lg:px-8 xl:px-10 pt-8 pb-6 border-b border-border bg-card">
        <div className="max-w-screen-xl mx-auto">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-6 h-6 rounded-lg gradient-primary flex items-center justify-center">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="white" strokeWidth="1.6" strokeLinecap="round">
                <circle cx="6" cy="6" r="2.5" />
                <path d="M6 1v1.5M6 9.5V11M1 6h1.5M9.5 6H11M2.64 2.64l1.06 1.06M8.3 8.3l1.06 1.06M2.64 9.36l1.06-1.06M8.3 3.7l1.06-1.06" />
              </svg>
            </div>
            <span className="badge badge-violet">Preferences</span>
          </div>
          <h1 className="font-extrabold text-foreground" style={{ fontSize: '26px', letterSpacing: '-0.02em' }}>Settings</h1>
          <p className="text-muted-foreground mt-0.5" style={{ fontSize: '14px' }}>
            Configure analytics tracking preferences and export your QR history data.
          </p>
        </div>
      </div>

      <div className="px-6 lg:px-8 xl:px-10 py-6">
        <div className="max-w-screen-xl mx-auto space-y-6">

          {/* ── Summary Strip ── */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {[
              { label: 'Tracking Features', value: `${enabledCount} / 5 active`, accent: '#EFF6FF', color: '#2563EB', icon: (
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="#2563EB" strokeWidth="1.7" strokeLinecap="round"><circle cx="9" cy="9" r="3.5" /><path d="M9 2v2M9 14v2M2 9h2M14 9h2M4.2 4.2l1.4 1.4M12.4 12.4l1.4 1.4M4.2 13.8l1.4-1.4M12.4 5.6l1.4-1.4" /></svg>
              )},
              { label: 'QR Codes Tracked', value: `${historyRecords.length} records`, accent: '#F5F3FF', color: '#7C3AED', icon: (
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="#7C3AED" strokeWidth="1.7" strokeLinecap="round"><rect x="2" y="2" width="5" height="5" rx="1" /><rect x="11" y="2" width="5" height="5" rx="1" /><rect x="2" y="11" width="5" height="5" rx="1" /><path d="M11 11h2M15 11h.5M11 13.5h1.5M13.5 13.5H15M11 16h4" /></svg>
              )},
              { label: 'Total Downloads', value: `${totalDownloads} files`, accent: '#F0FDF4', color: '#16A34A', icon: (
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="#16A34A" strokeWidth="1.7" strokeLinecap="round"><path d="M9 11V3M9 11L6 8M9 11L12 8" /><path d="M2 15h14" /></svg>
              )},
            ].map((item) => (
              <div key={item.label} className="bg-card rounded-3xl border border-border shadow-card p-5 flex items-center gap-4">
                <div className="w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: item.accent }}>
                  {item.icon}
                </div>
                <div className="min-w-0">
                  <p className="text-muted-foreground font-medium truncate" style={{ fontSize: '11px' }}>{item.label}</p>
                  <p className="font-bold text-foreground mt-0.5" style={{ fontSize: '16px', letterSpacing: '-0.01em' }}>{item.value}</p>
                </div>
              </div>
            ))}
          </div>

          {/* ── Analytics Tracking Preferences ── */}
          <SectionCard
            title="Analytics Tracking"
            description="Choose which events are recorded for your QR codes"
            accent="#EFF6FF"
            icon={<svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="#2563EB" strokeWidth="1.7" strokeLinecap="round"><path d="M2 14L6 9L10 11L16 4" /><circle cx="16" cy="4" r="1.5" fill="#2563EB" stroke="none" /></svg>}
          >
            <TrackingRow
              label="Scan Tracking"
              description="Record every time a QR code is scanned, with timestamps"
              checked={prefs.scanTracking}
              onChange={v => updatePref('scanTracking', v)}
            />
            <TrackingRow
              label="Download Tracking"
              description="Track how many times each QR was downloaded (PNG, SVG, PDF)"
              checked={prefs.downloadTracking}
              onChange={v => updatePref('downloadTracking', v)}
            />
            <TrackingRow
              label="Link Click Tracking"
              description="Monitor clicks on shareable links generated per QR code"
              checked={prefs.linkTracking}
              onChange={v => updatePref('linkTracking', v)}
            />
            <TrackingRow
              label="Device & Browser Tracking"
              description="Capture device type, OS, and browser info on each scan"
              checked={prefs.deviceTracking}
              onChange={v => updatePref('deviceTracking', v)}
            />
            <TrackingRow
              label="Location Tracking"
              description="Record approximate geographic location of scans (country/city)"
              checked={prefs.locationTracking}
              onChange={v => updatePref('locationTracking', v)}
            />
          </SectionCard>

          {/* ── Privacy & Data ── */}
          <SectionCard
            title="Privacy & Data"
            description="Control how your analytics data is stored and processed"
            accent="#F5F3FF"
            icon={<svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="#7C3AED" strokeWidth="1.7" strokeLinecap="round"><path d="M9 1.5L16.5 5.5V9C16.5 13 13 16.2 9 17C5 16.2 1.5 13 1.5 9V5.5L9 1.5Z" /><path d="M6.5 9L8.2 10.8L11.5 7.5" /></svg>}
          >
            <TrackingRow
              label="Anonymize Data"
              description="Strip personally identifiable information from all tracked events"
              checked={prefs.anonymizeData}
              onChange={v => updatePref('anonymizeData', v)}
            />

            {/* Retention Period */}
            <div className="pt-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="font-semibold text-foreground" style={{ fontSize: '14px' }}>Data Retention Period</p>
                  <p className="text-muted-foreground mt-0.5" style={{ fontSize: '12px' }}>How long to keep historical scan and download data</p>
                </div>
                <span className="font-bold text-primary" style={{ fontSize: '14px' }}>
                  {prefs.retentionDays === 365 ? '1 year' : prefs.retentionDays === 730 ? '2 years' : `${prefs.retentionDays} days`}
                </span>
              </div>
              <div className="flex gap-2 flex-wrap">
                {[30, 60, 90, 180, 365, 730].map(days => (
                  <button
                    key={days}
                    onClick={() => updatePref('retentionDays', days)}
                    className="px-3.5 py-2 rounded-xl font-semibold transition-all duration-150"
                    style={{
                      fontSize: '12px',
                      background: prefs.retentionDays === days ? 'linear-gradient(135deg, #2563EB 0%, #7C3AED 100%)' : 'var(--secondary)',
                      color: prefs.retentionDays === days ? '#FFFFFF' : 'var(--muted-foreground)',
                      border: prefs.retentionDays === days ? 'none' : '1px solid var(--border)',
                      boxShadow: prefs.retentionDays === days ? '0 4px 12px rgba(37,99,235,0.25)' : 'none',
                    }}
                  >
                    {days === 365 ? '1 yr' : days === 730 ? '2 yr' : `${days}d`}
                  </button>
                ))}
              </div>
            </div>
          </SectionCard>

          {/* ── Export Historical Data ── */}
          <SectionCard
            title="Export QR History"
            description="Download all your QR code records as a CSV file"
            accent="#F0FDF4"
            icon={<svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="#16A34A" strokeWidth="1.7" strokeLinecap="round"><path d="M9 11V3M9 11L6 8M9 11L12 8" /><path d="M2 15h14" /></svg>}
          >
            {/* Preview Table */}
            <div className="rounded-2xl border border-border overflow-hidden mb-5">
              <div className="overflow-x-auto">
                <table className="w-full" style={{ fontSize: '12px' }}>
                  <thead>
                    <tr style={{ background: 'var(--secondary)' }}>
                      {['ID', 'Type', 'Content', 'Created', 'Downloads'].map(h => (
                        <th key={h} className="text-left px-4 py-3 font-semibold text-muted-foreground" style={{ whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {historyRecords.slice(0, 5).map((r, i) => (
                      <tr key={r.id} style={{ background: i % 2 === 0 ? 'var(--card)' : 'var(--background)' }}>
                        <td className="px-4 py-3 font-mono text-muted-foreground" style={{ fontSize: '11px' }}>{r.id}</td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-0.5 rounded-full font-semibold capitalize" style={{ fontSize: '11px', background: 'var(--secondary)', color: 'var(--foreground)' }}>
                            {r.type}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-foreground" style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {r.content}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground" style={{ whiteSpace: 'nowrap' }}>
                          {new Date(r.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </td>
                        <td className="px-4 py-3 font-semibold text-foreground text-center">{r.downloadCount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {historyRecords.length > 5 && (
                <div className="px-4 py-2.5 border-t border-border" style={{ background: 'var(--secondary)' }}>
                  <p className="text-muted-foreground" style={{ fontSize: '11px' }}>
                    + {historyRecords.length - 5} more records will be included in the export
                  </p>
                </div>
              )}
            </div>

            {/* Export Info */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-start gap-3 p-4 rounded-2xl" style={{ background: 'var(--secondary)', flex: 1 }}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="var(--muted-foreground)" strokeWidth="1.6" strokeLinecap="round" className="flex-shrink-0 mt-0.5">
                  <circle cx="8" cy="8" r="6.5" /><path d="M8 7v4M8 5.5v.5" />
                </svg>
                <div>
                  <p className="font-semibold text-foreground" style={{ fontSize: '13px' }}>
                    {historyRecords.length} records · CSV format
                  </p>
                  <p className="text-muted-foreground mt-0.5" style={{ fontSize: '12px' }}>
                    Includes ID, type, content, creation date, download count, and QR options
                  </p>
                </div>
              </div>

              <button
                onClick={handleExportCSV}
                disabled={exportLoading}
                className="flex items-center gap-2.5 px-6 py-3 rounded-2xl font-semibold transition-all duration-200 flex-shrink-0"
                style={{
                  fontSize: '14px',
                  background: exportSuccess
                    ? 'linear-gradient(135deg, #16A34A 0%, #15803D 100%)'
                    : 'linear-gradient(135deg, #2563EB 0%, #7C3AED 100%)',
                  color: '#FFFFFF',
                  boxShadow: exportSuccess
                    ? '0 4px 14px rgba(22,163,74,0.30)'
                    : '0 4px 14px rgba(37,99,235,0.30)',
                  opacity: exportLoading ? 0.7 : 1,
                  cursor: exportLoading ? 'not-allowed' : 'pointer',
                  border: 'none',
                  minWidth: '160px',
                  justifyContent: 'center',
                }}
              >
                {exportLoading ? (
                  <>
                    <svg className="animate-spin" width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round">
                      <path d="M8 2a6 6 0 0 1 6 6" />
                    </svg>
                    Exporting…
                  </>
                ) : exportSuccess ? (
                  <>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round">
                      <path d="M3 8l3.5 3.5L13 5" />
                    </svg>
                    Downloaded!
                  </>
                ) : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round">
                      <path d="M8 10V3M8 10L5.5 7.5M8 10L10.5 7.5" /><path d="M2 13h12" />
                    </svg>
                    Export CSV
                  </>
                )}
              </button>
            </div>
          </SectionCard>

          {/* ── Save Button ── */}
          <div className="flex items-center justify-between gap-4 bg-card rounded-3xl border border-border shadow-card px-6 py-5">
            <div>
              <p className="font-semibold text-foreground" style={{ fontSize: '14px' }}>Save Preferences</p>
              <p className="text-muted-foreground mt-0.5" style={{ fontSize: '12px' }}>Your tracking settings are stored locally on this device</p>
            </div>
            <button
              onClick={handleSave}
              className="flex items-center gap-2.5 px-6 py-3 rounded-2xl font-semibold transition-all duration-200"
              style={{
                fontSize: '14px',
                background: saved
                  ? 'linear-gradient(135deg, #16A34A 0%, #15803D 100%)'
                  : 'linear-gradient(135deg, #2563EB 0%, #7C3AED 100%)',
                color: '#FFFFFF',
                boxShadow: saved
                  ? '0 4px 14px rgba(22,163,74,0.30)'
                  : '0 4px 14px rgba(37,99,235,0.30)',
                border: 'none',
                cursor: 'pointer',
                minWidth: '140px',
                justifyContent: 'center',
              }}
            >
              {saved ? (
                <>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round">
                    <path d="M3 8l3.5 3.5L13 5" />
                  </svg>
                  Saved!
                </>
              ) : (
                <>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round">
                    <path d="M13 10v2.5a.5.5 0 0 1-.5.5h-9a.5.5 0 0 1-.5-.5V10M8 9V3M8 9L5.5 6.5M8 9L10.5 6.5" />
                  </svg>
                  Save Changes
                </>
              )}
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}
